import { useState } from 'react';
import { goi, LoiApi } from '../chung/api';
import { useHoiLai } from '../chung/hoi-lai';
import type { NguoiDung, NhanVien, VaiTro } from '../chung/kieu';
import { DaiLoi, DaiTot, HopHoi, Khung, O } from './manh';

const TEN_VAI: Record<VaiTro, string> = {
  SELLER: 'Tư vấn',
  CASHIER: 'Thu ngân',
  ADMIN: 'Quản lý',
  WAREHOUSER: 'Kho',
};

/**
 * Nhân viên — tạo tài khoản, cho nghỉ / nhận lại, đặt lại mật khẩu.
 *
 * 🔴 KHÔNG BAO GIỜ XOÁ tài khoản, chỉ khoá.
 *
 *    Xoá là mất lịch sử đơn của người đó, mà đó là dữ liệu tính thưởng. Nên
 *    giao diện này cũng không có nút xoá — không phải quên, mà là cố ý.
 *
 * 🔴 Mật khẩu chỉ đi MỘT CHIỀU: gõ vào rồi gửi đi, không đọc lại được.
 *    Server không bao giờ trả mật khẩu, và màn này cũng không giữ lại sau khi
 *    gửi xong. Quên thì admin đặt lại — cửa hàng không thu email nhân viên nên
 *    không có luồng "quên mật khẩu" tự động.
 */
export function MucNhanVien({ toi }: { toi: NguoiDung }) {
  const ds = useHoiLai<NhanVien[]>('/api/nhan-vien', 15_000);
  const [tot, datTot] = useState<string | null>(null);
  const [taoMoi, datTaoMoi] = useState(false);
  const [doiTrangThai, datDoiTrangThai] = useState<NhanVien | null>(null);
  const [datLai, datDatLai] = useState<NhanVien | null>(null);

  const nv = ds.duLieu ?? [];
  const dangLam = nv.filter((n) => n.active);
  const daNghi = nv.filter((n) => !n.active);

  return (
    <Khung
      tieuDe="Nhân viên"
      phu={
        ds.duLieu
          ? `${dangLam.length} đang làm${daNghi.length ? ` · ${daNghi.length} đã nghỉ` : ''}`
          : 'Đang tải…'
      }
      hanhDong={
        <button className="nut-chinh" onClick={() => datTaoMoi(true)}>
          + Tạo tài khoản
        </button>
      }
    >
      <DaiLoi loi={ds.loi} />
      <DaiTot chu={tot} />

      {!ds.duLieu && [0, 1, 2].map((i) => <div key={i} className="xuong" style={{ height: 60, marginBottom: 12 }} />)}

      {[
        { ten: 'Đang làm', ds: dangLam },
        { ten: 'Đã nghỉ', ds: daNghi },
      ].map(
        (nhom) =>
          nhom.ds.length > 0 && (
            <div key={nhom.ten}>
              <div className="nhan-chu" style={{ margin: '10px 4px 12px' }}>
                {nhom.ten}
              </div>
              {nhom.ds.map((n, i) => (
                <div
                  key={n.id}
                  className="noi-nho truot-phai"
                  style={{
                    animationDelay: `${Math.min(i, 8) * 30}ms`,
                    padding: '14px 18px',
                    marginBottom: 12,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 16,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontWeight: 600 }}>{n.fullName}</span>
                      <span className="the the-mo">{TEN_VAI[n.role]}</span>
                      {n.owner && <span className="the the-cho">Chủ</span>}
                      {n.id === toi.id && <span className="the the-nhan">Bạn</span>}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--chu-3)', marginTop: 4 }}>
                      {n.username} · tạo{' '}
                      {new Date(n.createdAt).toLocaleDateString('vi-VN')}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <button onClick={() => datDatLai(n)} style={{ padding: '9px 13px', fontSize: 13 }}>
                      Đặt lại mật khẩu
                    </button>
                    {/*
                      Admin không tự khoá mình được — server chặn, nhưng để nút
                      sáng rồi mới báo lỗi là giao diện tồi.
                    */}
                    <button
                      onClick={() => datDoiTrangThai(n)}
                      disabled={n.id === toi.id}
                      className={n.active ? 'nut-nguy' : undefined}
                      style={{ padding: '9px 13px', fontSize: 13 }}
                    >
                      {n.active ? 'Khoá tài khoản' : 'Mở khoá'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ),
      )}

      {taoMoi && (
        <HopTaoNhanVien
          dong={() => datTaoMoi(false)}
          xong={(c) => {
            datTaoMoi(false);
            datTot(c);
            ds.taiLai();
          }}
        />
      )}
      {doiTrangThai && (
        <HopDoiTrangThai
          nv={doiTrangThai}
          dong={() => datDoiTrangThai(null)}
          xong={(c) => {
            datDoiTrangThai(null);
            datTot(c);
            ds.taiLai();
          }}
        />
      )}
      {datLai && (
        <HopDatLaiMatKhau
          nv={datLai}
          dong={() => datDatLai(null)}
          xong={(c) => {
            datDatLai(null);
            datTot(c);
          }}
        />
      )}
    </Khung>
  );
}

// ───────────────────────── tạo tài khoản ─────────────────────────

/** Chỉ chữ THƯỜNG, số, và `. _ -` — 3..64. Luật của server, chép đúng. */
const MAU_USERNAME = /^[a-z0-9._-]{3,64}$/;

function HopTaoNhanVien({ dong, xong }: { dong: () => void; xong: (c: string) => void }) {
  const [username, datUsername] = useState('');
  const [hoTen, datHoTen] = useState('');
  const [vaiTro, datVaiTro] = useState<VaiTro>('SELLER');
  const [matKhau, datMatKhau] = useState('');
  const [dangLam, datDangLam] = useState(false);
  const [loi, datLoi] = useState<string | null>(null);

  const uHopLe = MAU_USERNAME.test(username);
  const mHopLe = matKhau.length >= 8;
  const duoc = uHopLe && mHopLe && hoTen.trim() !== '';

  const tao = async () => {
    if (!duoc || dangLam) return;
    datDangLam(true);
    datLoi(null);
    try {
      await goi('/api/nhan-vien', {
        method: 'POST',
        than: { username, mat_khau: matKhau, ho_ten: hoTen.trim(), vai_tro: vaiTro },
      });
      // Không giữ lại mật khẩu sau khi gửi, và không hiện lại nó ở bất kỳ đâu.
      datMatKhau('');
      xong(`Đã tạo tài khoản "${username}" cho ${hoTen.trim()}.`);
    } catch (e) {
      datLoi(e instanceof LoiApi ? e.message : 'Không tạo được.');
      datDangLam(false);
    }
  };

  return (
    <HopHoi tieuDe="Tạo tài khoản" nutY="TẠO" dangLam={dangLam} dong={dong} dongY={() => void tao()} choPhep={duoc}>
      <DaiLoi loi={loi} />
      <O nhan="Họ tên ★">
        <input value={hoTen} onChange={(e) => datHoTen(e.target.value)} placeholder="Trần Thị B" />
      </O>
      <O nhan="Tên đăng nhập ★">
        <input
          value={username}
          // Tự hạ chữ thường ngay khi gõ. Bàn phím tiếng Việt trên điện thoại hay
          // tự viết hoa chữ đầu, và server thì cấm chữ hoa — để người dùng gõ
          // "Hoa" rồi báo lỗi là bắt họ đoán luật.
          onChange={(e) => datUsername(e.target.value.toLowerCase().replace(/\s/g, ''))}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          placeholder="ban-hang-3"
        />
      </O>
      {username !== '' && !uHopLe && (
        <div style={{ color: 'var(--nhan)', fontWeight: 600, fontSize: 'var(--chu-nho)', marginTop: -8, marginBottom: 16 }}>
          Chỉ chữ thường, số, và <b>. _ -</b> — từ 3 đến 64 ký tự. Không dấu tiếng Việt.
        </div>
      )}
      <O nhan="Vai trò ★">
        {/* Lưới 2×2, không phải một hàng bốn nút: "Quản lý kho" dài gấp đôi ba
            nhãn kia, nhồi một hàng ở bề ngang 420px là chữ gãy dòng. */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {(['SELLER', 'CASHIER', 'ADMIN', 'WAREHOUSER'] as VaiTro[]).map((v) => (
            <button
              key={v}
              onClick={() => datVaiTro(v)}
              className={vaiTro === v ? 'lom-nho' : undefined}
              style={{ padding: 13, fontWeight: 700, color: vaiTro === v ? 'var(--nhan)' : undefined }}
            >
              {TEN_VAI[v]}
            </button>
          ))}
        </div>
      </O>
      <O nhan="Mật khẩu ★ — ít nhất 8 ký tự">
        <input
          type="password"
          value={matKhau}
          onChange={(e) => datMatKhau(e.target.value)}
          autoComplete="new-password"
        />
      </O>
    </HopHoi>
  );
}

// ───────────────────────── nghỉ / nhận lại ─────────────────────────

function HopDoiTrangThai({ nv, dong, xong }: { nv: NhanVien; dong: () => void; xong: (c: string) => void }) {
  const [dangLam, datDangLam] = useState(false);
  const [loi, datLoi] = useState<string | null>(null);
  const khoa = nv.active;

  const lam = async () => {
    datDangLam(true);
    datLoi(null);
    try {
      await goi(`/api/nhan-vien/${nv.id}/trang-thai`, { method: 'PATCH', than: { active: !nv.active } });
      xong(khoa ? `Đã khoá tài khoản ${nv.fullName}.` : `Đã mở khoá ${nv.fullName}.`);
    } catch (e) {
      datLoi(e instanceof LoiApi ? e.message : 'Không đổi được.');
      datDangLam(false);
    }
  };

  return (
    <HopHoi
      tieuDe={khoa ? `Khoá tài khoản ${nv.fullName}?` : `Mở khoá ${nv.fullName}?`}
      nutY={khoa ? 'KHOÁ' : 'MỞ KHOÁ'}
      nguyHiem={khoa}
      dangLam={dangLam}
      dong={dong}
      dongY={() => void lam()}
    >
      <DaiLoi loi={loi} />
      {khoa ? (
        <>
          <b>{nv.username}</b> sẽ không đăng nhập được nữa, <b>có hiệu lực ngay</b> — kể cả khi
          họ đang mở màn hình. Những dòng hàng họ đã thêm vẫn nằm nguyên trong giỏ, ai cũng
          xử lý tiếp được.
          <div style={{ marginTop: 12 }}>
            Tài khoản <b>không bị xoá</b>: thành tích đã ghi của họ là dữ liệu tính lương.
            Mở khoá lúc nào cũng được.
          </div>
        </>
      ) : (
        <>
          <b>{nv.username}</b> đăng nhập lại được ngay với mật khẩu cũ. Quên mật khẩu thì dùng
          nút <b>Đặt lại mật khẩu</b>.
        </>
      )}
    </HopHoi>
  );
}

// ───────────────────────── đặt lại mật khẩu ─────────────────────────

function HopDatLaiMatKhau({ nv, dong, xong }: { nv: NhanVien; dong: () => void; xong: (c: string) => void }) {
  const [moi, datMoi] = useState('');
  const [dangLam, datDangLam] = useState(false);
  const [loi, datLoi] = useState<string | null>(null);
  const duoc = moi.length >= 8;

  const lam = async () => {
    if (!duoc || dangLam) return;
    datDangLam(true);
    datLoi(null);
    try {
      await goi(`/api/nhan-vien/${nv.id}/dat-lai-mat-khau`, { method: 'POST', than: { mat_khau_moi: moi } });
      datMoi('');
      xong(`Đã đặt lại mật khẩu cho ${nv.fullName}. Đọc cho họ rồi bảo họ tự đổi.`);
    } catch (e) {
      datLoi(e instanceof LoiApi ? e.message : 'Không đặt lại được.');
      datDangLam(false);
    }
  };

  return (
    <HopHoi
      tieuDe={`Đặt lại mật khẩu — ${nv.fullName}`}
      nutY="ĐẶT LẠI"
      dangLam={dangLam}
      dong={dong}
      dongY={() => void lam()}
      choPhep={duoc}
    >
      <DaiLoi loi={loi} />
      <O nhan="Mật khẩu mới — ít nhất 8 ký tự">
        <input type="password" value={moi} onChange={(e) => datMoi(e.target.value)} autoComplete="new-password" autoFocus />
      </O>
      <div style={{ fontSize: 12, color: 'var(--chu-3)', lineHeight: 1.65 }}>
        Phiên đang mở của <b>{nv.username}</b> vẫn chạy tới khi hết hạn; mật khẩu mới dùng cho
        lần đăng nhập sau. Hệ thống <b>không hiện lại</b> mật khẩu này lần nào nữa — đọc cho họ
        ngay bây giờ.
      </div>
    </HopHoi>
  );
}
